/* eslint-disable indent */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Input, Table, Row, Col, message, Button } from "antd";
import { CheckOutlined, WarningOutlined } from "@ant-design/icons";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import beepSound from "../../assests/audio/beep.mp3";
import urls from "../../api/urls";
import API from "../../api";
import Heading from "../../components/Heading";
import "./style.scss";

// Custom hook for barcode scanner detection - Single Responsibility Principle
const useBarcodeScanner = (onScan, dependencies = []) => {
  const barcodeRef = useRef("");
  const intervalRef = useRef();

  // HTML5 API / Intent Integration for CipherLab and other enterprise browsers
  useEffect(() => {
    // 1. Global JS function injection (CipherLab "Web/HTML5" mode can trigger this)
    window.onBarcodeScanned = (barcode) => {
      if (barcode && typeof barcode === "string") {
        onScan(barcode.trim());
      }
    };
    window.onScanBarcode = window.onBarcodeScanned;
    window.CipherLabScan = window.onBarcodeScanned;

    // 2. Custom DOM Events (dispatched by some Enterprise Browsers or Wedge apps)
    const handleCustomScanEvent = (e) => {
      // Trying to find barcode data in common event detail paths
      const barcode =
        e.detail?.barcode ||
        e.detail?.data ||
        e.detail?.value ||
        e.detail ||
        e.data;
      if (typeof barcode === "string" && barcode.trim()) {
        onScan(barcode.trim());
      }
    };

    document.addEventListener("barcodeScanned", handleCustomScanEvent);
    document.addEventListener("cipherlab.barcode", handleCustomScanEvent);
    document.addEventListener("scannerCB", handleCustomScanEvent);

    return () => {
      window.onBarcodeScanned = undefined;
      window.onScanBarcode = undefined;
      window.CipherLabScan = undefined;
      document.removeEventListener("barcodeScanned", handleCustomScanEvent);
      document.removeEventListener("cipherlab.barcode", handleCustomScanEvent);
      document.removeEventListener("scannerCB", handleCustomScanEvent);
    };
  }, [onScan]);
  const handleKeyDown = useCallback(
    (event) => {
      // If user is typing in an editable field, let the field handle it
      const target = event.target;
      const isEditableTarget =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (isEditableTarget) {
        return;
      }

      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Handle Enter key - complete barcode scan
      if (event.code === "Enter") {
        if (barcodeRef.current.trim()) {
          onScan(barcodeRef.current.trim());
          barcodeRef.current = "";
        }
        return;
      }

      // Ignore all non-printable keys and special keys
      const ignoredKeys = [
        "Shift",
        "Control",
        "Alt",
        "Meta",
        "CapsLock",
        "NumLock",
        "ScrollLock",
        "Tab",
        "Escape",
        "F1",
        "F2",
        "F3",
        "F4",
        "F5",
        "F6",
        "F7",
        "F8",
        "F9",
        "F10",
        "F11",
        "F12",
        "Insert",
        "Delete",
        "Home",
        "End",
        "PageUp",
        "PageDown",
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Pause",
        "PrintScreen",
        "ContextMenu",
      ];

      // Check if it's a special key or non-printable character
      if (
        ignoredKeys.includes(event.key) ||
        event.key.length > 1 || // Keys like "Enter", "Backspace", etc. have length > 1
        event.ctrlKey ||
        event.altKey ||
        event.metaKey
      ) {
        return;
      }

      // Only append alphanumeric characters to the buffered barcode
      if (event.key.length === 1 && /^[a-zA-Z0-9]$/.test(event.key)) {
        barcodeRef.current += event.key;
      }

      // Reset barcode after 150ms of inactivity to support slower Android wedge emulators
      intervalRef.current = setTimeout(() => {
        barcodeRef.current = "";
      }, 150);
    },
    [onScan, ...dependencies],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [handleKeyDown]);
};

// Utility function to mask AWB number (e.g., 25003300057151 -> 2500XXXXXX7151)
const maskAWBNumber = (awbNumber) => {
  if (!awbNumber || awbNumber.length < 8) return awbNumber;
  const awbStr = awbNumber.toString();
  const firstFour = awbStr.substring(0, 4);
  const lastFour = awbStr.substring(awbStr.length - 4);
  const maskedMiddle = "X".repeat(awbStr.length - 8);
  return `${firstFour}${maskedMiddle}${lastFour}`;
};

// Custom hook for managing scanned AWB data - Open/Closed Principle
const useAWBManager = () => {
  // Load data from localStorage on component mount
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem("inScanData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        return {
          outScannedData: parsed.outScannedData || [],
          notForCPData: parsed.notForCPData || [],
        };
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
    return { outScannedData: [], notForCPData: [] };
  };

  const initialData = loadFromStorage();
  const [outScannedData, setOutScannedData] = useState(
    initialData.outScannedData,
  );
  const [notForCPData, setNotForCPData] = useState(initialData.notForCPData);

  // Create scannedAWBs Set from existing data with consistent formatting
  const [scannedAWBs, setScannedAWBs] = useState(() => {
    const allAWBs = [
      ...initialData.outScannedData.map((item) =>
        item.awbNumber?.toString().trim(),
      ),
      ...initialData.notForCPData.map((item) =>
        item.awbNumber?.toString().trim(),
      ),
    ].filter(Boolean); // Remove any undefined/null values
    return new Set(allAWBs);
  });

  // Save to localStorage whenever data changes
  const saveToStorage = useCallback((outData, notForCPData) => {
    try {
      const dataToSave = {
        outScannedData: outData,
        notForCPData: notForCPData,
        lastUpdated: new Date().toISOString(),
      };
      localStorage.setItem("inScanData", JSON.stringify(dataToSave));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, []);

  const addAWBRecord = useCallback(
    (rawAwbNumber, data) => {
      // Ensure AWB number is consistently formatted
      const awbNumber = rawAwbNumber?.toString().trim();

      // if already present in either list, skip to prevent duplicates
      if (scannedAWBs.has(awbNumber)) {
        return;
      }

      const newRecord = {
        ...data,
        id: `${awbNumber}-${Date.now()}`,
        awbNumber,
        isUnmasked: true,
        scannedAt: new Date().toISOString(),
      };

      if (data?.outScannedForCP) {
        setOutScannedData((prev) => {
          const exists = prev.some((r) => r.awbNumber === awbNumber);
          const newData = exists ? prev : [newRecord, ...prev];
          saveToStorage(newData, notForCPData);
          return newData;
        });
      } else {
        setNotForCPData((prev) => {
          const exists = prev.some((r) => r.awbNumber === awbNumber);
          const newData = exists ? prev : [newRecord, ...prev];
          saveToStorage(outScannedData, newData);
          return newData;
        });
      }

      setScannedAWBs((prev) => new Set([...prev, awbNumber]));
    },
    [saveToStorage, outScannedData, notForCPData, scannedAWBs],
  );

  const unmaskAWBRecord = useCallback(
    (rawAwbNumber) => {
      const awbNumber = rawAwbNumber?.toString().trim();

      // Update in outScannedData
      setOutScannedData((prev) => {
        const updated = prev.map((item) =>
          item.awbNumber === awbNumber
            ? { ...item, isUnmasked: true, scannedAt: new Date().toISOString() }
            : item,
        );
        saveToStorage(updated, notForCPData);
        return updated;
      });

      // Update in notForCPData
      setNotForCPData((prev) => {
        const updated = prev.map((item) =>
          item.awbNumber === awbNumber
            ? { ...item, isUnmasked: true, scannedAt: new Date().toISOString() }
            : item,
        );
        saveToStorage(outScannedData, updated);
        return updated;
      });
    },
    [saveToStorage, outScannedData, notForCPData],
  );

  const clearAllData = useCallback(() => {
    setOutScannedData([]);
    setNotForCPData([]);
    setScannedAWBs(new Set());
    try {
      localStorage.removeItem("inScanData");
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  }, []);

  const isAWBScanned = useCallback(
    (rawAwbNumber) => {
      // Ensure consistent formatting for checking
      const awbNumber = rawAwbNumber?.toString().trim();
      return scannedAWBs.has(awbNumber);
    },
    [scannedAWBs],
  );

  return {
    outScannedData,
    notForCPData,
    addAWBRecord,
    unmaskAWBRecord,
    isAWBScanned,
    scannedCount: scannedAWBs.size,
    clearAllData,
  };
};

const InScan = () => {
  const [currentAWB, setCurrentAWB] = useState("");
  const [loading, setLoading] = useState(false);
  const [scanErrors, setScanErrors] = useState(new Set());
  const [outscannedShipmentsList, setOutscannedShipmentsList] = useState([]);
  const [fetchingOutscanned, setFetchingOutscanned] = useState(false);

  const {
    outScannedData,
    notForCPData,
    addAWBRecord,
    unmaskAWBRecord,
    isAWBScanned,
    scannedCount,
    clearAllData,
  } = useAWBManager();

  // Function to play beep sound
  const playBeepSound = useCallback(() => {
    try {
      const audio = new Audio(beepSound);
      audio.volume = 0.5; // Set volume to 50%
      audio.play().catch((error) => {
        console.log("Audio play failed:", error);
      });
    } catch (error) {
      console.log("Audio creation failed:", error);
    }
  }, []);

  // Fetch outscanned shipments on component mount
  const fetchOutscannedShipments = useCallback(async () => {
    try {
      setFetchingOutscanned(true);

      // Get cpId from userinfo dynamically to ensure it checks after login
      const currentUserinfo = JSON.parse(localStorage.getItem("userinfo"));
      const cpId = currentUserinfo?.cpId;

      if (!cpId) {
        console.error("CP ID not found in userinfo");
        return;
      }

      const response = await API.Api(
        `${urls.CP_PRS_OUTSCANNED_SHIPMENTS}/${cpId}`,
      ).get();

      if (response.status && response.response?.data?.data) {
        const shipments = response.response.data.data.map((shipment) => ({
          id: `outscanned-${shipment.documentNumber}`,
          awbNumber: shipment.documentNumber,
          outScannedDetails: shipment.outScannedDetails,
          isUnmasked: true, // Set to true by default as requested
          cpDetails: shipment.cpDetails || null,
          outScannedForCP: true,
        }));
        setOutscannedShipmentsList(shipments);
      }
    } catch (error) {
      console.error("Error fetching outscanned shipments:", error);
    } finally {
      setFetchingOutscanned(false);
    }
  }, []);

  // Fetch outscanned shipments on mount
  useEffect(() => {
    fetchOutscannedShipments();
  }, [fetchOutscannedShipments]);

  // API service following Dependency Inversion Principle
  const scanAWB = useCallback(async (awbNumber) => {
    try {
      setLoading(true);

      // Get userinfo dynamically
      const currentUserinfo = JSON.parse(localStorage.getItem("userinfo"));
      // Get spId from premise or user context
      const spId = parseInt(currentUserinfo?.spId) || null;

      // API call to inscan endpoint
      const response = await API.Api(urls.CP_PRS_INSCAN).postIdHeader({
        awbNumber,
        spId: spId,
        sevasetuUserId: currentUserinfo?.sevasetuUserId,
        role: currentUserinfo?.roles?.[0]?.replace("ROLE_", ""),
        dpId: (() => {
          const deliveryPartners =
            JSON.parse(sessionStorage.getItem("deliveryPartner"))?.data || [];
          return deliveryPartners.length > 0 ? deliveryPartners[0]?.dpId : null;
        })(),
      });

      // Return the actual API response structure
      const responseData = response.response?.data;

      const isSuccess =
        response.status &&
        String(response.response?.status).startsWith("2") &&
        (!responseData?.status || String(responseData?.status).startsWith("2"));

      return {
        status: isSuccess,
        data: responseData,
        message: responseData?.message,
        apiResponse: response.response,
      };
    } catch (error) {
      console.error("API Error:", error);
      const responseData = error?.response?.data;
      return {
        status: false,
        message:
          responseData?.message || "AWB not found or not outscanned from hub",
        error: error.message || "Failed to scan AWB",
        apiResponse: error.response || null,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle AWB scan with validation and deduplication
  const handleAWBScan = useCallback(
    async (rawAwbNumber) => {
      // Clean and validate AWB number – alphanumeric only
      const awbNumber = rawAwbNumber
        ?.toString()
        .replace(/[^a-zA-Z0-9]/g, "")
        .trim();

      if (!awbNumber || awbNumber.length === 0) {
        message.error("Please enter a valid AWB number");
        return;
      }

      // Accept both numeric and alphanumeric formats
      const awbPattern = /^[a-zA-Z0-9]+$/;
      if (!awbPattern.test(awbNumber)) {
        message.error("AWB number should contain valid characters");
        return;
      }

      // Check if AWB is in the outscanned list (masked)
      const outscannedShipment = outscannedShipmentsList.find(
        (ship) => ship.awbNumber === awbNumber,
      );

      // if (outscannedShipment && !outscannedShipment.isUnmasked) {
      //   // Unmask the AWB in the outscanned list
      //   setOutscannedShipmentsList((prev) =>
      //     prev.map((ship) =>
      //       ship.awbNumber.toUpperCase() === awbNumber
      //         ? { ...ship, isUnmasked: true }
      //         : ship
      //     )
      //   );
      // }

      // Check for duplicates in scanned data
      if (isAWBScanned(awbNumber)) {
        message.warning(`AWB ${awbNumber} has already been scanned`);
        return;
      }

      // Process scan via API
      const result = await scanAWB(awbNumber);

      if (result.status) {
        // Merge cpDetails from outscanned list if available
        const dataWithCPDetails = {
          ...result?.data,
          cpDetails: outscannedShipment?.cpDetails || result?.data?.cpDetails,
          outScannedDetails:
            outscannedShipment?.outScannedDetails ||
            result?.data?.outScannedDetails,
          outScannedForCP:
            outscannedShipment?.outScannedForCP ||
            result?.data?.outScannedForCP,
        };

        addAWBRecord(awbNumber, dataWithCPDetails);
        playBeepSound(); // Play beep sound on successful scan
        message.success(
          result.message ||
            result.apiResponse?.data?.message ||
            `AWB ${awbNumber} scanned successfully`,
        );
      } else {
        const errorMessage =
          result.error ||
          result.message ||
          result.apiResponse?.data?.message ||
          result.apiResponse?.message ||
          "Failed to scan AWB";

        // Add error to the set for persistent display
        setScanErrors((prev) => {
          const newErrors = new Set(prev);
          const errorObj = {
            awbNumber,
            error: errorMessage,
            timestamp: new Date().toISOString(),
          };
          newErrors.add(errorObj);
          return newErrors;
        });

        // Also show toast for immediate feedback
        message.error(errorMessage, 3); // Show for 3 seconds
      }
      setCurrentAWB(""); // Clear input after any scan attempt
    },
    [
      scanAWB,
      addAWBRecord,
      isAWBScanned,
      playBeepSound,
      outscannedShipmentsList,
    ],
  );

  // Set up barcode scanner
  useBarcodeScanner(handleAWBScan, [handleAWBScan]);

  // Handle In Scan Complete
  const handleInScanComplete = useCallback(() => {
    if (scannedCount === 0 && scanErrors.size === 0) {
      message.warning("No data to clear");
      return;
    }

    clearAllData();
    setScanErrors(new Set());
    message.success("In-Scan completed!");
  }, [clearAllData, scannedCount, scanErrors]);

  // Table columns configuration
  const tableColumns = [
    // {
    //   title: "Sl No",
    //   align: "center",
    //   render: (_, __, index) => index + 1,
    //   width: 80,
    // },
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      render: (text, record) => (
        <div className="d-flex align-center">
          <span className="awb-number">
            {record.isUnmasked ? text : maskAWBNumber(text)}
            {/* {record.isUnmasked && (
              <CheckOutlined
                style={{
                  marginLeft: "8px",
                  color: "#fff",
                  backgroundColor: "#52c41a",
                  borderRadius: "50%",
                  padding: "2px",
                  fontSize: "12px",
                }}
              />
            )} */}
          </span>
        </div>
      ),
    },
    {
      title: "CP Name",
      dataIndex: ["cpDetails", "cpName"],
      key: "cpName",
      render: (text, record) => {
        const currentCpName = JSON.parse(localStorage.getItem("userId"))
          ?.premises?.premiseName;
        const cpName = record.outScannedForCP ? currentCpName : text;
        return (
          <div className="d-flex align-center">
            <span className="cp-name">{cpName ? cpName : "-"}</span>
          </div>
        );
      },
    },
    {
      title: "Out-Scan Hub",
      dataIndex: ["outScannedDetails", "hubName"],
      key: "outScannedDetails",
      render: (text) => (
        <div className="d-flex align-center">
          <span className="out-scanned-details">{text ? text : "-"}</span>
        </div>
      ),
    },
    {
      title: "Is For Your CP?",
      dataIndex: "outScannedForCP",
      key: "outScannedForCP",
      align: "center",
      width: 150,
      render: (outScannedForCP) => (
        <span
          className={`status-tag ${outScannedForCP ? "for-cp" : "not-for-cp"}`}
        >
          {outScannedForCP ? "Yes" : "No"}
        </span>
      ),
    },
  ];

  const tableLoading = {
    spinning: loading,
    indicator: (
      <img
        src={Smcs_Loader}
        style={{ height: "100px", width: "100px" }}
        alt="Loading"
      />
    ),
  };

  return (
    <div className="inscan-container">
      <Row justify="space-between" className="header-row">
        <Heading title="In-Scan Shipments" />
      </Row>

      <div className="inscan-content">
        {/* AWB Input Section */}
        <div className="awb-input-section">
          <Row justify="center" className="input-row">
            <Col xs={24} sm={20} md={16} lg={12}>
              <div className="awb-input-container">
                <Input
                  size="large"
                  placeholder="Scan AWB number using barcode scanner..."
                  value={currentAWB}
                  className="awb-input"
                  onChange={(e) => {
                    // Keep only alphanumeric characters
                    const cleanedValue = e.target.value.replace(
                      /[^a-zA-Z0-9]/g,
                      "",
                    );
                    setCurrentAWB(cleanedValue);
                  }}
                  onPressEnter={() => {
                    handleAWBScan(currentAWB);
                    setCurrentAWB("");
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "10px",
                    alignItems: "flex-end", // Align bottom to match button and text
                  }}
                >
                  <div className="scan-info" style={{ marginTop: 0 }}>
                    <span className="scan-count">
                      Total Scanned Shipments: <strong>{scannedCount}</strong>
                    </span>
                  </div>
                  <div
                    className="complete-inscan-section"
                    style={{ marginTop: 0, textAlign: "right" }}
                  >
                    <div className="button-info">
                      <span
                        className="info-text"
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          textAlign: "right",
                        }}
                      >
                        Click to Complete In-Scan session and clear all scanned
                        records.
                      </span>
                      <Button
                        type="primary"
                        danger
                        size="large"
                        onClick={handleInScanComplete}
                        disabled={scannedCount === 0}
                        className="complete-button"
                      >
                        Complete In-Scan
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Side by Side Tables */}
        <div className="tables-section">
          <div className="parent-section">
            <div className="parent-header">
              <h2>Shipments List</h2>
            </div>
            <Row gutter={[16, 16]} className="tables-row">
              <Col xs={24}>
                <div className="table-container">
                  <Table
                    rowKey="id"
                    className="scan-table"
                    columns={tableColumns}
                    dataSource={[
                      ...outscannedShipmentsList.filter(
                        (item) => !isAWBScanned(item.awbNumber),
                      ),
                      ...outScannedData,
                      ...notForCPData,
                    ].sort((a, b) => {
                      if (!a.scannedAt && !b.scannedAt) return 0;
                      if (!a.scannedAt) return 1; // Pending items go to bottom
                      if (!b.scannedAt) return -1;
                      return (
                        new Date(b.scannedAt).getTime() -
                        new Date(a.scannedAt).getTime()
                      );
                    })}
                    loading={tableLoading || fetchingOutscanned}
                    pagination={{
                      pageSize: 10,
                      showTotal: (total) => `Total ${total} items`,
                    }}
                    style={{
                      fontSize: "14px",
                    }}
                    scroll={{ y: 400 }}
                    size="small"
                  />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Errors Section */}
        {console.log(
          "Rendering errors section, scanErrors size:",
          scanErrors.size,
        )}
        {scanErrors.size > 0 && (
          <div className="errors-section">
            <div className="errors-header">
              <WarningOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
              <span className="errors-title">
                Scan Errors ({scanErrors.size})
              </span>
              <Button
                type="text"
                size="small"
                onClick={() => setScanErrors(new Set())}
                style={{ marginLeft: "auto", color: "#ff4d4f" }}
              >
                Clear All Errors
              </Button>
            </div>
            <div className="errors-list">
              {Array.from(scanErrors).map((error, index) => (
                <div key={index} className="error-item">
                  <div className="error-awb">
                    <strong>AWB: {error.awbNumber}</strong>
                  </div>
                  <div className="error-message">{error.error}</div>
                  <div className="error-time">
                    {new Date(error.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InScan;
